/**
 * Admin glassmorphism design tokens.
 * Enterprise command-center aesthetic — dark deep-space base, indigo/violet/cyan accent.
 */

export const ADMIN_COLORS = {
  // Base backgrounds
  darkBase: '#0B0F1E',
  lightBase: '#F6F7FB',

  // Accent gradient stops
  accentIndigo: '#6366F1',
  accentViolet: '#8B5CF6',
  accentCyan: '#22D3EE',

  // Semantic
  danger: '#F43F5E',
  success: '#10B981',
  warning: '#F59E0B',
  info: '#38BDF8',
};

export const ADMIN_GRADIENTS = {
  accent: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #22D3EE 100%)',
  accentShort: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
  accentHorizontal: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 50%, #22D3EE 100%)',
  danger: 'linear-gradient(135deg, #F43F5E 0%, #E11D48 100%)',
  success: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
  warning: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  darkBase: 'linear-gradient(180deg, #0B0F1E 0%, #0F1525 100%)',
};

/**
 * Returns glass surface tokens for the given MUI theme.
 * Usage: const g = glass(theme);
 */
export function glass(theme) {
  const isDark = theme.palette.mode === 'dark';

  return {
    surface: isDark
      ? {
          background: 'rgba(15,18,32,0.55)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(2,6,23,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
          backdropFilter: 'blur(18px) saturate(140%)',
          WebkitBackdropFilter: 'blur(18px) saturate(140%)',
        }
      : {
          background: 'rgba(255,255,255,0.55)',
          border: '1px solid rgba(15,23,42,0.08)',
          boxShadow: '0 8px 32px rgba(15,23,42,0.08)',
          backdropFilter: 'blur(18px) saturate(140%)',
          WebkitBackdropFilter: 'blur(18px) saturate(140%)',
        },

    surfaceStrong: isDark
      ? {
          background: 'rgba(15,18,32,0.75)',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 16px 48px rgba(2,6,23,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
          backdropFilter: 'blur(24px) saturate(160%)',
          WebkitBackdropFilter: 'blur(24px) saturate(160%)',
        }
      : {
          background: 'rgba(255,255,255,0.75)',
          border: '1px solid rgba(15,23,42,0.12)',
          boxShadow: '0 16px 48px rgba(15,23,42,0.12)',
          backdropFilter: 'blur(24px) saturate(160%)',
          WebkitBackdropFilter: 'blur(24px) saturate(160%)',
        },

    // For table row zebra / subtle fills — no backdrop-filter on rows
    subtleFill: isDark
      ? 'rgba(255,255,255,0.025)'
      : 'rgba(15,23,42,0.025)',

    rowHover: isDark
      ? 'rgba(99,102,241,0.08)'
      : 'rgba(99,102,241,0.05)',

    headBg: isDark
      ? 'rgba(15,18,32,0.8)'
      : 'rgba(246,247,251,0.9)',

    border: isDark
      ? 'rgba(255,255,255,0.08)'
      : 'rgba(15,23,42,0.08)',

    borderStrong: isDark
      ? 'rgba(255,255,255,0.14)'
      : 'rgba(15,23,42,0.14)',
  };
}

export const gradients = ADMIN_GRADIENTS;
